import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BasketSeverity, BasketView } from "@trader-dashboard/shared";
import { formatCurrency } from "@/utils/formatCurrency";
import { AlertChip } from "./AlertChip";

type BasketCardProps = {
  basket: BasketView;
};

const STRIPE_CLASSES: Record<BasketSeverity, string> = {
  none: "border-l-gray-300",
  watch: "border-l-amber-400",
  yellow: "border-l-orange-500",
  depth: "border-l-purple-500",
  red: "border-l-red-500",
};

const SIDE_CHIP: Record<BasketView["side"], { label: string; classes: string }> = {
  buy: { label: "BUY", classes: "bg-sky-100 text-sky-800" },
  sell: { label: "SELL", classes: "bg-rose-100 text-rose-800" },
  unknown: { label: "?", classes: "bg-gray-100 text-gray-500" },
};

const Metric = ({
  label,
  value,
  pending,
}: {
  label: string;
  value: string;
  pending: boolean;
}): React.ReactNode => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    {pending ? (
      <div className="mt-1 h-4 w-12 animate-pulse rounded bg-muted" />
    ) : (
      <p className="text-sm font-semibold tabular-nums">{value}</p>
    )}
  </div>
);

const formatPips = (pips: number | null): string =>
  pips === null ? "—" : `${Math.round(pips)}p`;

export const BasketCard = ({ basket }: BasketCardProps): React.ReactNode => {
  const side = SIDE_CHIP[basket.side];
  // Money from the live feed is exact; a value-factor estimate is marked ≈,
  // and figures straight off stale synced rows are flagged as such.
  const ddPrefix = basket.ddSource === "estimated" ? "≈" : "";
  const detailPending = basket.detailPending;

  return (
    <Card className={`border-l-4 ${STRIPE_CLASSES[basket.severity]}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
        <div className="flex items-center gap-2">
          <span className="text-base font-bold">{basket.symbol}</span>
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${side.classes}`}>
            {side.label}
          </span>
        </div>
        {basket.depthNoPain && (
          <span
            className={`rounded-md bg-purple-600 px-2 py-0.5 text-[11px] font-bold text-white ${
              basket.chfEmphasis ? "ring-2 ring-purple-300 animate-pulse" : ""
            }`}
          >
            DEPTH — no pain{basket.chfEmphasis ? " · CHF" : ""}
          </span>
        )}
      </CardHeader>
      <CardContent className="p-4 pt-2">
        <div className="grid grid-cols-3 gap-x-3 gap-y-2 sm:grid-cols-4">
          <Metric label="DD $" value={`${ddPrefix}${formatCurrency(basket.ddUsd)}`} pending={false} />
          <Metric label="Orders" value={`${basket.orders}`} pending={false} />
          <Metric
            label="DD/Order $"
            value={`${ddPrefix}${formatCurrency(basket.ddPerOrder)}`}
            pending={false}
          />
          <Metric label="vs wavg" value={formatPips(basket.pipsFromWavg)} pending={detailPending} />
          <Metric label="vs 1st entry" value={formatPips(basket.pipsFromFirst)} pending={detailPending} />
          <Metric
            label="Age (days)"
            value={basket.ageDays === null ? "—" : basket.ageDays.toFixed(1)}
            pending={detailPending}
          />
          <Metric
            label="Total lots"
            value={basket.totalLots === null ? "—" : basket.totalLots.toFixed(2)}
            pending={detailPending}
          />
        </div>
        {(basket.chips.length > 0 || basket.partialSync || basket.ddSource === "synced") && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {basket.chips.map((chip) => (
              <AlertChip key={chip.channel} label={chip.label} tone={chip.tone} />
            ))}
            {basket.partialSync && (
              <span className="text-[11px] text-muted-foreground">
                {basket.syncedOrders}/{basket.orders} synced
              </span>
            )}
            {basket.ddSource === "synced" && (
              <span className="text-[11px] text-muted-foreground">stale P/L</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
